/**
 * "My Projects" section (About page) — static, code-managed content.
 *
 * Previously backed by a MongoDB CMS with an admin editor, built when the
 * project ran on a token-metered hosting environment and manual DB edits
 * were the only way to add content without burning credits. Now that the
 * repo is edited directly, code is simpler to reason about and avoids
 * CMS-editor limitations (e.g. image cropping bugs) — ask to add/edit an
 * entry here instead of through an admin panel.
 *
 * `summary` is the one-liner shown on the card; `description` is the long
 * copy, kept for later use. Text fields are { it, en } pairs so the section actually translates (a
 * previous version hardcoded Italian only, which silently stayed Italian on
 * the English page). image_url: local path served from frontend/public.
 * fit: 'cover' (fills the 16:10 card frame — good for wide screenshots) or
 * 'contain' (letterboxed with padding — good for logos and book covers that
 * must not be cropped).
 */
export const PROJECTS = [
  // Patrimony leads the list as the featured item (2 cols x 2 rows on lg, with
  // the next two cards stacked beside it); it and Gabremoku get a brand glow on
  // hover. Further projects just flow into a normal row below.
  {
    id: 'patrimony',
    featured: true, // rendered as the big bento card; keep it FIRST in the array
    title: { it: 'Patrimony', en: 'Patrimony' },
    subtitle: { it: 'Monitora il tuo patrimonio', en: 'Track your net worth' },
    description: {
      it: 'Una dashboard pulita per registrare, visualizzare e far crescere il proprio patrimonio mese dopo mese: liquidità, investimenti e previdenza in un unico posto, con storico temporale e confronto diretto con benchmark come lo S&P 500.',
      en: 'A clean dashboard to track, visualize and grow your net worth month after month: cash, investments and retirement accounts in one place, with a full history and direct comparison against benchmarks like the S&P 500.',
    },
    summary: {
      it: 'Dashboard per registrare e far crescere il patrimonio: liquidità, investimenti e previdenza in un unico posto.',
      en: 'A dashboard to track and grow your net worth: cash, investments and retirement in one place.',
    },
    image_url: '/projects/patrimony.jpg',
    fit: 'contain',
    link_url: 'https://www.getpatrimony.com/',
    link_text: { it: 'Scopri Patrimony', en: 'Discover Patrimony' },
  },
  {
    id: 'book-liberi-di-scegliere',
    title: { it: 'Liberi di Scegliere', en: 'Liberi di Scegliere' },
    subtitle: { it: 'Il mio libro', en: 'My book' },
    description: {
      it: 'Un percorso verso la consapevolezza finanziaria e la libertà personale. In questo libro racconto le scelte e gli errori che mi hanno portato dove sono oggi, e il passaggio da una mentalità fissa a una mentalità di crescita. Condivido il percorso e gli strumenti concreti per sbloccarsi e rimettersi in movimento verso le proprie scelte.',
      en: "A path toward financial awareness and personal freedom. In this book I share the choices and mistakes that brought me where I am today, and the shift from a fixed mindset to a growth mindset. I share the journey and the concrete tools to break free and start moving again toward your own choices.",
    },
    summary: {
      it: 'Le scelte e gli errori che mi hanno portato dove sono, e gli strumenti per rimettersi in movimento.',
      en: 'The choices and mistakes that brought me here, and the tools to get moving again.',
    },
    image_url: '/projects/liberi-di-scegliere.jpg',
    fit: 'contain',
    link_url: 'https://amzn.to/4vtX2qE',
    link_text: { it: 'Acquista su Amazon', en: 'Buy on Amazon' },
  },
  {
    id: 'gabremoku-indicators',
    title: { it: 'Gabremoku Indicators', en: 'Gabremoku Indicators' },
    subtitle: { it: 'I miei indicatori TradingView', en: 'My TradingView indicators' },
    description: {
      it: 'Una suite di indicatori proprietari per TradingView, tra cui il Market Cycle Wave utilizzato in questa dashboard: strumenti pensati per leggere la fase di mercato, il momentum e la volatilità con un approccio quantitativo e sistematico.',
      en: 'A suite of proprietary TradingView indicators, including the Market Cycle Wave used in this dashboard: tools designed to read market phase, momentum and volatility with a quantitative, systematic approach.',
    },
    summary: {
      it: 'Indicatori proprietari per TradingView, tra cui il Market Cycle Wave usato in questa dashboard.',
      en: 'Proprietary TradingView indicators, including the Market Cycle Wave used in this dashboard.',
    },
    image_url: '/projects/gabremoku-indicators.jpg',
    fit: 'contain',
    link_url: 'https://www.tradingview.com/u/Gabremoku/#published-scripts',
    link_text: { it: 'Vedi su TradingView', en: 'View on TradingView' },
  },
  {
    id: 'voidflare',
    wide: true, // full-width banner row under the bento: closes the grid (sm: 2 cols, xl: 3 cols)
    title: { it: 'voidflare', en: 'voidflare' },
    // Tagline kept in English on purpose in both languages (the owner's call: not translated).
    subtitle: { it: 'Craft your Conversion, Always Offline', en: 'Craft your Conversion, Always Offline' },
    subtitle_color: '#818cf8', // indigo, light enough for AA contrast on the dark card (the logo's #6366f1 is ~3.7:1)
    description: {
      it: 'Convertitori per Windows che lavorano interamente sul tuo PC: Word, Excel e presentazioni in PDF, DWG in PDF, immagini, audio, video ed e-book. Nessun file viene caricato online. Open source, costruito sui motori di ArtCraft.',
      en: 'Windows converters that work entirely on your own PC: Word, Excel and slides to PDF, DWG to PDF, images, audio, video and e-books. No file is uploaded anywhere. Open source, built on the ArtCraft engines.',
    },
    summary: {
      it: 'Convertitori offline per Windows: i tuoi file restano sul tuo PC. Open source, costruito sui motori di ArtCraft.',
      en: 'Offline converters for Windows: your files stay on your PC. Open source, built on the ArtCraft engines.',
    },
    image_url: '/projects/voidflare.jpg',
    fit: 'cover',
    link_url: 'https://voidflare.app/',
    link_text: { it: 'Scopri voidflare', en: 'Discover voidflare' },
  },
];

/**
 * "Lavori" section (About page): work done FOR others — client/freelance
 * work where the idea and the content belong to someone else and Gabriele's
 * part is stated explicitly. Not "My Projects" on purpose — never list these
 * there. Same shape as PROJECTS. `subtitle` carries the credit line, keep it
 * literal: Teo's own site thanks Gabriele for the website, nothing more.
 */
export const WORKS = [
  {
    id: 'velum-obscura',
    title: { it: 'Velum Obscura', en: 'Velum Obscura' },
    subtitle: { it: 'Gioco ideato e realizzato da Matteo Losito', en: 'Game designed and created by Matteo Losito' },
    description: {
      it: 'Sito vetrina del gioco di carte a ruoli nascosti ideato e realizzato da Matteo Losito, con regolamento e glossario consultabili online in italiano e inglese. Il gioco è interamente opera di Matteo Losito; di Gabriele Denaro sono il design e lo sviluppo del sito.',
      en: "Showcase website for the hidden-role card game designed and created by Matteo Losito, with the rulebook and glossary readable online in Italian and English. The game is entirely Matteo Losito's work; the website's design and development are by Gabriele Denaro.",
    },
    summary: {
      it: 'Sito vetrina: Design e sviluppo → Gabriele Denaro',
      en: 'Showcase site: Design and development → Gabriele Denaro',
    },
    image_url: '/projects/velum-obscura.jpg',
    fit: 'cover',
    link_url: 'https://velumobscura.com/',
    link_text: { it: 'Visita il sito', en: 'Visit the site' },
  },
];
